import React, { useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled, { keyframes } from 'styled-components';
import { useAskVantage } from '../../context/AskVantageProvider';
import { type ThemeConfig, useTheme } from '../../theme';
import TTSButton from '../TTSButton';
import { stripMarkdown } from '../../helpers/textExtraction';
import type { RootState } from '../../../../state/store';
import { useSelector } from 'react-redux';

const InterimAnswer: React.FC = () => {
  const { interimAnswer, activeConversationId } = useAskVantage();
  const theme = useTheme();
  const animationSpeed = 3; // configurable speed in seconds
  const { demo_account: isDemoAccount } = useSelector(
    (state: RootState) => state.company.company_details
  );
  // Generate a stable block ID for the interim answer
  const blockId = useMemo(() => {
    return `interim_${activeConversationId || 'new'}_${Date.now()}`;
  }, [activeConversationId]);

  if (!interimAnswer) {
    return null;
  }

  return (
    <Wrapper $theme={theme} animationSpeed={animationSpeed}>
      <div className="shiny-text">
        <Markdown remarkPlugins={[remarkGfm]}>{interimAnswer}</Markdown>
      </div>
      <ActionsContainer>
        {isDemoAccount && <TTSButton blockId={blockId} text={stripMarkdown(interimAnswer)} />}
      </ActionsContainer>
    </Wrapper>
  );
};

const shine = keyframes`
  0% {
    background-position: 100%;
  }
  100% {
    background-position: -100%;
  }
`;

export default InterimAnswer;

const Wrapper = styled.div<{ $theme: ThemeConfig; animationSpeed: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${(props) => props.$theme.tokens.spaceMd};

  .shiny-text {
    font-size: 14px;
    font-weight: 500;
    opacity: 0.6;
    background: linear-gradient(
      120deg,
      ${({ $theme }) => ($theme.mode === 'dark' ? '#b5b5b5a4' : $theme.colors.mainText + '99')} 40%,
      ${({ $theme }) =>
          $theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : $theme.colors.mainText + 'ff'}
        50%,
      ${({ $theme }) => ($theme.mode === 'dark' ? '#b5b5b5a4' : $theme.colors.mainText + '99')} 60%
    );
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    display: inline-block;
    animation: ${shine} ${(props) => props.animationSpeed}s linear infinite;
  }

  /* Fallback for browsers that don't support background-clip: text */
  @supports not (-webkit-background-clip: text) {
    .shiny-text {
      -webkit-text-fill-color: ${(props) => props.$theme.colors.mainText};
      color: ${(props) => props.$theme.colors.mainText};
      opacity: 0.6;
      animation: none;
    }
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`;
